import {useRef, useEffect, useCallback} from 'react';

/**
 * Abort controller hook<br/>
 * Will automatically abort the previous signal when a new signal is created
 * @example
 * const {newAbortSignal} = useAbortController();
 * const getOptions = useCallback(async (query: string) => {
 * 	const signal = newAbortSignal();
 * 	try {
 * 		return await getKeywordAutoList({query}, signal);
 * 	} catch (ex) {
 * 		return [];
 * 	}
 * }, []);
 */
export const useAbortController = () => {
	const abortController = useRef(new AbortController());
	const signal = useRef(abortController.current.signal);

	useEffect(() => () => abortController.current.abort(), []);

	const newAbortSignal = useCallback(() => {
		abortController.current.abort();
		abortController.current = new AbortController();
		signal.current = abortController.current.signal;
		return signal.current;
	}, []);

	useEffect(() => {
		return () => {
			!abortController.current.signal.aborted &&
				abortController.current.abort();
		};
	}, []);

	return {abortController, signal, newAbortSignal};
};

export default useAbortController;
